import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Tooltip
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { prestadorFetch } from '../../contexts/PrestadorAuthContext';
import CalendarioDialog from '../calendario/CalendarioDialog';
import TarefasPanel from '../tarefas/TarefasPanel';

export default function PrestadorTopBarActions() {
  const [alertas, setAlertas] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [tarefasOpen, setTarefasOpen] = useState(false);
  const [contratos, setContratos] = useState([]);

  useEffect(() => {
    prestadorFetch('/api/prestador/contratos')
      .then((r) => (r.ok ? r.json() : []))
      .then((lista) => setContratos(Array.isArray(lista) ? lista : []));
  }, []);

  const carregarAlertas = useCallback(async () => {
    try {
      const res = await prestadorFetch('/api/prestador/alertas');
      if (res.ok) {
        const data = await res.json();
        setAlertas(Array.isArray(data) ? data : []);
      }
    } catch {
      setAlertas([]);
    }
  }, []);

  useEffect(() => {
    carregarAlertas();
    const t = setInterval(carregarAlertas, 60000);
    return () => clearInterval(t);
  }, [carregarAlertas]);

  const naoLidos = alertas.filter((a) => !a.lido).length;

  const fetchEventos = async (params) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await prestadorFetch(`/api/prestador/calendario/eventos?${q.toString()}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao carregar calendário');
    }
    return res.json();
  };

  const criarEvento = async (payload) => {
    const res = await prestadorFetch('/api/prestador/calendario/eventos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao criar evento');
    carregarAlertas();
  };

  const atualizarEvento = async (id, payload) => {
    const res = await prestadorFetch(`/api/prestador/calendario/eventos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar evento');
    carregarAlertas();
  };

  const excluirEvento = async (id) => {
    const res = await prestadorFetch(`/api/prestador/calendario/eventos/${id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir evento');
    carregarAlertas();
  };

  const marcarLido = async (id) => {
    await prestadorFetch(`/api/prestador/alertas/${id}/lido`, { method: 'PATCH' });
    carregarAlertas();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Calendário">
        <IconButton color="inherit" onClick={() => setCalOpen(true)} aria-label="Calendário">
          <CalendarTodayIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Tarefas">
        <IconButton color="inherit" onClick={() => setTarefasOpen(true)} aria-label="Tarefas">
          <TaskAltIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Notificações">
        <IconButton color="inherit" onClick={(e) => setNotifAnchor(e.currentTarget)} aria-label="Notificações">
          <Badge badgeContent={naoLidos} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 320, maxWidth: 400 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ px: 1 }}>
            Notificações
          </Typography>
          <Divider />
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {alertas.length === 0 ? (
              <ListItem>
                <ListItemText primary="Nenhum alerta." />
              </ListItem>
            ) : (
              alertas.map((a) => (
                <ListItem
                  key={a.id}
                  sx={{ bgcolor: a.lido ? 'transparent' : 'action.hover', cursor: 'pointer' }}
                  onClick={() => {
                    if (!a.lido) marcarLido(a.id);
                  }}
                >
                  <ListItemText
                    primary={a.titulo}
                    secondary={a.mensagem}
                    primaryTypographyProps={{ fontWeight: a.lido ? 400 : 700 }}
                  />
                </ListItem>
              ))
            )}
          </List>
          <Button size="small" fullWidth onClick={() => setCalOpen(true)} sx={{ mt: 1 }}>
            Abrir calendário
          </Button>
        </Box>
      </Popover>

      <CalendarioDialog
        open={calOpen}
        onClose={() => setCalOpen(false)}
        fetchEventos={fetchEventos}
        criarEvento={criarEvento}
        atualizarEvento={atualizarEvento}
        excluirEvento={excluirEvento}
        modoPrestador
        contratos={contratos}
      />

      {tarefasOpen && (
        <TarefasPanel open={tarefasOpen} onClose={() => setTarefasOpen(false)} modo="prestador" />
      )}
    </Box>
  );
}
